import { useLoaderData } from "react-router";
import Feed from "@models/feed";
import ProductsRail from "@components/ProductRail/ProductsRail";
import HeroBannerSlider from "@components/HeroBannerSlider/HeroBannerSlider";
import CategoryRow from "@components/CategoryRow/CategoryRow";
import { popularCategories, importantCategories } from '../../data/categories';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useEffect, useRef, useMemo } from 'react';
import { useHeroBanner } from '../../contexts/HeroBannerContext';

export default function Feeds() {
  const feeds = useLoaderData<Feed[]>();
  const hasScrolledRef = useRef(false);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const { setHasHeroBanner } = useHeroBanner();
  
  usePageTitle({
    title: 'OnePlay Store | Buy Cheap Games, Game Keys, Steam Wallets and Much More at the cheapest price.',
    description: 'Buy Cheap Games, Steam Gift Cards, Game Keys everything in one single place - get the best prices and exclusive game deals daily in OnePlay Store now!'
  });

  // Separate hero banner feeds from regular feeds
  const { heroBannerFeeds, regularFeeds } = useMemo(() => {
    const heroBanners = feeds.filter((feed) => feed.type === "hero_banner");
    const regular = feeds.filter((feed) => feed.type !== "hero_banner");
    return {
      heroBannerFeeds: heroBanners,
      regularFeeds: regular,
    };
  }, [feeds]);

  // Update hero banner context
  useEffect(() => {
    // If feeds is empty or no hero banners found, set to false to show static banner fallback
    const hasHeroBanners = feeds && Array.isArray(feeds) && feeds.length > 0 && heroBannerFeeds.length > 0;
    setHasHeroBanner(hasHeroBanners);
    return () => {
      setHasHeroBanner(false);
    };
  }, [feeds, heroBannerFeeds.length, setHasHeroBanner]);

  // Disable scroll restoration to prevent browser from interfering
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Handle hash navigation to scroll to the target collection section
  useEffect(() => {
    // Only proceed if feeds are loaded
    if (!feeds || feeds.length === 0) return;

    const hash = window.location.hash;
    if (!hash) {
      hasScrolledRef.current = false;
      return;
    }

    // Reset scroll flag for new hash
    hasScrolledRef.current = false;

    const targetId = hash.substring(1); // Remove the '#' symbol
    
    const scrollToTarget = () => {
      const targetElement = document.getElementById(targetId);
      
      if (targetElement && targetElement.offsetParent !== null) {
        // Element exists and is visible
        // Get navbar height to account for fixed positioning
        const navbar = document.querySelector('.custom-navbar') as HTMLElement;
        const navbarHeight = navbar ? navbar.offsetHeight : 72; // Default to 72px if navbar not found
        const offset = navbarHeight + 18; // Add 18px padding for better visibility
        
        // Calculate the target scroll position accounting for navbar
        const targetPosition = targetElement.offsetTop - offset;
        
        // Use multiple methods to ensure scroll happens
        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: 'auto' // Use 'auto' for immediate scroll on initial load
        });
        
        // Also set directly as fallback
        document.documentElement.scrollTop = Math.max(0, targetPosition);
        document.body.scrollTop = Math.max(0, targetPosition);
        
        hasScrolledRef.current = true;
        return true; // Success
      }
      return false; // Element not found or not visible
    };

    // Try scrolling immediately
    if (scrollToTarget()) {
      return;
    }

    // If element not found, use MutationObserver to wait for it
    const observer = new MutationObserver(() => {
      if (hasScrolledRef.current) {
        observer.disconnect();
        return;
      }
      if (scrollToTarget()) {
        observer.disconnect();
      }
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also use multiple timeout attempts as fallback
    const attempts = [50, 100, 200, 500, 1000, 2000];
    attempts.forEach((delay, index) => {
      const timeoutId = setTimeout(() => {
        if (hasScrolledRef.current) {
          return; // Already scrolled, skip
        }
        if (scrollToTarget()) {
          observer.disconnect();
          // Clear remaining timeouts
          attempts.slice(index + 1).forEach((_, i) => {
            if (timeoutRefs.current[index + i + 1]) {
              clearTimeout(timeoutRefs.current[index + i + 1]);
            }
          });
        }
      }, delay);
      timeoutRefs.current[index] = timeoutId;
    });

    // Cleanup
    return () => {
      observer.disconnect();
      timeoutRefs.current.forEach(clearTimeout);
      timeoutRefs.current = [];
    };
  }, [feeds]);

  // Handle hash changes (for when user clicks links while on the page)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const targetId = hash.substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const navbar = document.querySelector('.custom-navbar') as HTMLElement;
          const navbarHeight = navbar ? navbar.offsetHeight : 72;
          const offset = navbarHeight + 18;
          
          // Use smooth scroll for hash changes (user interactions)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const targetPosition = targetElement.offsetTop - offset;
              window.scrollTo({
                top: Math.max(0, targetPosition),
                behavior: 'smooth'
              });
            });
          });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [feeds]);

  // Ensure feeds is an array before rendering
  // If feeds is empty, still render to allow static banner fallback to show
  if (!feeds || !Array.isArray(feeds)) {
    return null;
  }
  
  // If feeds array is empty, still render nothing but static banner will show via Banner component
  if (feeds.length === 0) {
    return null;
  }

  return (
    <>
      {/* Render Hero Banner Sliders */}
      {heroBannerFeeds.map((feed) => (
        <HeroBannerSlider key={feed.id} feed={feed} />
      ))}
      
      {/* Render Category Row */}
      {/* <CategoryRow categories={popularCategories} /> */}

      {/* Render Regular Product Rails */}
      {regularFeeds.map((feed) => (
        <ProductsRail key={feed.id} feed={feed} />
      ))}
    </>
  );
}
